import React, { ChangeEvent, memo, ReactElement, ReactNode, useEffect, useState } from 'react';
import { FormFeedback, Input, Label } from 'reactstrap';
import { uniqueId } from 'lodash';
import { Box } from '@mui/material';
import { Validator, Validators } from '@payer-admin-ui/components-v2/src/inputs/validation';

const styles = {
  input: {
    margin: '.1rem 0',
  },
  label: {
    display: 'block',
    margin: 0,
  },
  feedback: {
    display: 'block',
    margin: 0,
    lineHeight: '1.5em',
    minHeight: '1.5em',
  },
};

export type TextInputProps = {
  title: ReactNode;
  value: string | null | undefined;
  setValue: (value: string | null) => void;
  placeholder?: string;
  validators?: Validator<string>[];
  disabled?: boolean;
  onValidationUpdate?: (validationError: null | ReactElement) => void;
};

const TextInput = ({
  title,
  value,
  setValue,
  placeholder = '',
  validators = [],
  disabled = false,
  onValidationUpdate,
}: TextInputProps) => {
  const [id] = useState(() => uniqueId('TextInput-'));

  // Run the validation once at init to generate default value here, to get validation going before first onChange call.
  const [validationError, setValidationError] = useState<null | ReactElement>(() =>
    Validators.validate(value, validators),
  );

  useEffect(() => {
    setValidationError(Validators.validate(value, validators));
  }, [value, validators]);

  // Update the encapsulating component with any validation error so that it can react (disable buttons etc)
  useEffect(() => {
    onValidationUpdate?.(validationError);
  }, [onValidationUpdate, validationError]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue?.(event.target.value || null);
  };

  return (
    <Box sx={styles.input}>
      <Label style={styles.label}>
        {title && <div>{title}</div>}
        <Input
          id={id}
          type="text"
          placeholder={placeholder}
          value={value ?? ''}
          onChange={handleOnChange}
          invalid={!!validationError}
          disabled={disabled}
        />
        <FormFeedback style={styles.feedback}>{validationError}</FormFeedback>
      </Label>
    </Box>
  );
};

const memoizedTextInput = memo(TextInput) as typeof TextInput;
export { memoizedTextInput as TextInput };
